import React, {useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import SettingStore from '../../../../../../stores/setting-store';
import {ExchangeLocationsAddRule} from '../exchange-locations-add-rule/exchange-locations-add-rule';
import './exchange-locations-section.scss';
import ExchangeLocationsTable from "../exchange-locations-table/exchange-locations-table";
import ExchangeLocationsRulePopup from '../exchange-locations-rule-popup/exchange-locations-rule-popup';
import ExchangeLocationsRuleModalStore, {MODAL_MODE} from '../../stores/exchange-locations-rule-modal-store';
import {IExchangeLocationRuleRow} from '../../utilities/exchange-locations-table-builder';

interface IExchangeLocationsSectionProps {
    settingStore: SettingStore;
}

/**
 * Main section component for exchange locations configuration.
 * Displays rules table and controls for adding/editing location rules.
 */
const ExchangeLocationsSection: React.FC<IExchangeLocationsSectionProps> = observer(({settingStore}: IExchangeLocationsSectionProps) => {

    /** Modal state */
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRow, setEditingRow] = useState<IExchangeLocationRuleRow | null>(null);

    /** Load warehouse and zone options on mount or store recreation*/
    useEffect(() => {
        settingStore.exchangeLocationsStore.load();
    }, [settingStore.exchangeLocationsStore]);

    /** Create modal store based on mode (ADD or EDIT/EDIT_FALLBACK) */
    const modalStore = useMemo(() => {
        if (!isModalOpen) {
            return null;
        }

        /** Determine mode based on editingRow */
        let mode = MODAL_MODE.ADD;
        if (editingRow) {
            mode = editingRow.is_fallback ? MODAL_MODE.EDIT_FALLBACK : MODAL_MODE.EDIT;
        }

        return new ExchangeLocationsRuleModalStore(
            mode,
            editingRow,
            settingStore.exchangeLocationsStore
        );
    }, [isModalOpen, editingRow, settingStore.exchangeLocationsStore]);

    /** Handle opening modal for adding new rule */
    const handleOpenModal = () => {
        setEditingRow(null);
        setIsModalOpen(true);
    };

    /** Handle opening modal for editing existing rule */
    const handleEditRow = (row: IExchangeLocationRuleRow) => {
        setEditingRow(row);
        setIsModalOpen(true);
    };

    /** Handle closing modal */
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingRow(null);
    };

    /** Handle apply button - save changes */
    const handleApply = () => {
        if (modalStore) {
            modalStore.apply();
            setIsModalOpen(false);
            setEditingRow(null);
        }
    };

    return (
        <div className="exchange-locations-section">
            <ExchangeLocationsAddRule
                onActionClick={handleOpenModal}
                actionDisabled={settingStore.exchangeLocationsStore.areAllWarehousesUsed()}
                actionLoading={settingStore.exchangeLocationsStore.isLoading()}
            />

            <ExchangeLocationsTable
                store={settingStore.exchangeLocationsStore}
                onRowEdit={handleEditRow}
            />

            {modalStore && (
                <ExchangeLocationsRulePopup
                    onClose={handleCloseModal}
                    onApply={handleApply}
                    modalStore={modalStore}
                />
            )}
        </div>
    );
});

export default ExchangeLocationsSection;

